import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
/**
 * Renders the markdown-ish text Fireflies returns (bullet_gist, action_items,
 * short_summary) as readable blocks instead of one flat string:
 *
 *   • lines beginning with -, *, • or a "1." style number become list items
 *   • **bold** spans render bold (Fireflies uses them as speaker/topic headers)
 *   • a blank line starts a new block
 *   • every other newline is a line break
 *
 * Presentational and dependency-free so it can be previewed in the QA harness.
 */
const BULLET_RE = /^\s*(?:[-*•]|\d+[.)])\s+/;
const BOLD_RE = /\*\*(.+?)\*\*/g;
/**
 * Fireflies' bullet_gist lines come through as "📦 August Launch: 50+ products
 * go live…" — an emoji, a short topic label, then the detail. Bold the label so
 * the list scans, but only for that exact shape: an emoji lead-in and a colon
 * early in the line, so a normal sentence containing a colon is left alone.
 */
const LEAD_LABEL_RE = /^(\p{Extended_Pictographic}️?\s*)([^:]{1,40}):\s+(.*)$/u;
function emphasizeLeadLabel(line) {
    if (line.includes('**'))
        return line; // already marked up
    const m = LEAD_LABEL_RE.exec(line);
    if (!m)
        return line;
    const [, lead, label, rest] = m;
    return `${lead}**${label}:** ${rest}`;
}
/** Split a line into plain / bold runs so **text** renders bold. */
function renderInline(line, keyPrefix) {
    const nodes = [];
    let lastIndex = 0;
    let match;
    BOLD_RE.lastIndex = 0;
    while ((match = BOLD_RE.exec(line)) !== null) {
        if (match.index > lastIndex) {
            nodes.push(line.slice(lastIndex, match.index));
        }
        nodes.push(_jsx(Box, { component: "strong", sx: { fontWeight: 600, color: '#344054' }, children: match[1] }, `${keyPrefix}-b-${match.index}`));
        lastIndex = match.index + match[0].length;
    }
    if (lastIndex < line.length)
        nodes.push(line.slice(lastIndex));
    return nodes.length ? nodes : [line];
}
/** Group consecutive bullet lines into lists, everything else into text blocks. */
function parseBlocks(raw) {
    const blocks = [];
    let current = null;
    for (const rawLine of raw.split(/\r?\n/)) {
        const line = rawLine.trimEnd();
        // Blank line closes the current block.
        if (!line.trim()) {
            if (current)
                blocks.push(current);
            current = null;
            continue;
        }
        if (BULLET_RE.test(line)) {
            const item = line.replace(BULLET_RE, '').trim();
            if (current?.kind === 'list')
                current.items.push(item);
            else {
                if (current)
                    blocks.push(current);
                current = { kind: 'list', items: [item] };
            }
        }
        else {
            if (current?.kind === 'text')
                current.lines.push(line.trim());
            else {
                if (current)
                    blocks.push(current);
                current = { kind: 'text', lines: [line.trim()] };
            }
        }
    }
    if (current)
        blocks.push(current);
    return blocks;
}
const FormattedMeetingText = ({ text, emptyText = '—', fontSize = '12px', }) => {
    const blocks = useMemo(() => (text ? parseBlocks(text) : []), [text]);
    if (!blocks.length) {
        return (_jsx(Typography, { sx: { fontSize: `${fontSize} !important`, color: '#98A2B3' }, children: emptyText }));
    }
    return (_jsx(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 0.75 }, children: blocks.map((block, bi) => block.kind === 'list' ? (_jsx(Box, { component: "ul", sx: { m: 0, pl: 2.25, display: 'flex', flexDirection: 'column', gap: 0.5 }, children: block.items.map((item, ii) => (_jsx(Typography, { component: "li", sx: {
                    fontSize: `${fontSize} !important`,
                    color: '#475467',
                    lineHeight: 1.5,
                    '&::marker': { color: '#EE2A7B' },
                }, children: renderInline(emphasizeLeadLabel(item), `b-${bi}-i-${ii}`) }, `b-${bi}-i-${ii}`))) }, `b-${bi}`)) : (_jsx(Box, { children: block.lines.map((line, li) => (_jsx(Typography, { sx: { fontSize: `${fontSize} !important`, color: '#475467', lineHeight: 1.5 }, children: renderInline(emphasizeLeadLabel(line), `b-${bi}-l-${li}`) }, `b-${bi}-l-${li}`))) }, `b-${bi}`))) }));
};
export default FormattedMeetingText;
